import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import {FormLabel} from '@mui/material';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import _ from 'lodash';
import Notes from '../../../../util/notes';
import moment from 'moment';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {useDispatch, useSelector} from 'react-redux';
import {setSnackbar} from '../../../../store/reducers/globals';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import MessageBox from '../../../../util/messageBox';
import useOpenState from '../../../../hooks/useOpenState';
import API from '../../../../app/api';
import {cleaningRequestPath} from '../../../../store/reducers/ward';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';

const DEFAULT_FORM_DATA = {
    offWard: false,
    homeLeave: false,
    information: '',
    startDate: moment().toISOString(),
    endDate: null,
    completed: false,

    timestamp: null,
    who: '',
    login: '',

    bedReserveId: null,
    bedReserveName: null,
};

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'column',
        gap: 10,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
}));

const OffWardForm = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {patient, disableSave, open, onChange, onClose} = props;

    // Form state
    const [form, setForm] = useState(DEFAULT_FORM_DATA);
    const [formFilled, setFormFilled] = useState(false);
    const [modified, setModified] = useState(false);
    const [errorDate, setErrorDate] = useState({
        start: null,
        end: null,
    });

    // User
    const userObject = useSelector(user);
    const fullName = Formatter.formatUserFullName(userObject);

    // Open state
    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, true);
    const [isClearOpen, openClear, closeClear] = useOpenState(false, true);
    const [isWarningOpen, openWarning, closeWarning] = useOpenState(false, true);

    // Load Data
    useEffect(() => {
        if (patient?.offWard) {
            setForm(patient.offWard);
            setFormFilled(true);
        }
    }, []);

    const onSave = async (clearCompleted) => {
        closeWarning();
        let clone = _.cloneDeep(form);
        let bed;
        if (validation()) {
            if (patient?.bed) {
                bed = await API.getBed(patient.bed);
                if (bed && bed?.type !== 'wlist') {
                    clone.bedReserveId = patient.bed;
                    clone.bedReserveName = bed.name;
                }
                if (!clearCompleted) dispatch(cleaningRequestPath(patient?.bed, 'offWard'));
            }
            clone = setUser(clone);
            if (clearCompleted) clone.completed = false;
            onChange([{
                name: 'offWard',
                value: clone,
                noteToAdd: Notes.getOffWardNote(clone),
            }]);
            onClose();
        }
    };

    const handleSubmitButton = () => {
        if (formFilled && form.completed) {
            openWarning();
        } else {
            onSave(false);
        }
    };

    const validation = () => {
        if (!form.information) {
            dispatch(setSnackbar({type: 'warning', message: 'Please provide the information.'}));
            return false;
        } else if (!form.startDate || !form.endDate) {
            dispatch(setSnackbar({type: 'warning', message: 'Please provide start and end dates.'}));
            return false;
        } else if (errorDate.start || errorDate.end) {
            dispatch(setSnackbar({type: 'warning', message: 'Please enter the correct date and time.'}));
            return false;
        }
        return true;
    };

    const handleChange = (val, name) => {
        setModified(true);
        setForm(prevState => ({
            ...prevState,
            [name]: val,
        }));
    };

    const handleChangeEndDateByButton = (value) => {
        setModified(true);
        setForm(prevState => ({
            ...prevState,
            endDate: moment(prevState.startDate).add(value, 'hour'),
        }));
    };

    const renderButtons = () => {
        const buttons = [0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 24];
        return buttons.map(btn => (
            <AgyleButton
                variant="outlined"
                size="small"
                width={20}
                onClick={handleChangeEndDateByButton.bind(this, btn)}
                key={btn + ''}
            >
                {`+${btn}h`}
            </AgyleButton>
        ));
    };

    const handlePatientBack = () => {
        let clone = _.cloneDeep(form);
        clone.completed = true;
        if (validation()) {
            clone = setUser(clone);
            onChange([{
                name: 'offWard',
                value: clone,
                noteToAdd: Notes.getOffWardNote(clone),
            }]);
            onClose();
        }
    };

    const setUser = (data) => {
        data['timestamp'] = moment().toISOString();
        data['who'] = fullName;
        data['login'] = userObject?.userName;
        return data;
    };

    const handleClearData = () => {
        setForm(DEFAULT_FORM_DATA);
        setFormFilled(false);
        closeClear();
    };

    return (
        <>
            <MiniFormDialog
                title={t(props._formTitle)}
                open={open}
                onClose={onClose}
                disableSubmit={disableSave || (!form.offWard && !form.homeLeave) || !modified}
                onSave={handleSubmitButton}
                formOptions={props._formOptions}
                closeButtonOnHeader
                buttons={[
                    <AgyleButton
                        onClick={openClear}
                    >
                        {t('Clear')}
                    </AgyleButton>,
                ]}
            >
                <>
                    <div className={classes.container}>
                        <div className={classes.divRow}>
                            <FormLabel style={{minWidth: 95}}>
                                {t('Off Ward')}
                            </FormLabel>

                            <AgyleBlockSwitch
                                value={form.offWard}
                                onChange={(val) => {
                                    if (val) handleChange(false, 'homeLeave');
                                    handleChange(val, 'offWard');
                                }}
                            />

                            {(formFilled && !form.completed) &&
                                <div style={{marginLeft: 'auto'}}>
                                    <AgyleButton
                                        variant="contained"
                                        onClick={openConfirm}
                                    >
                                        {t('Patient back on ward')}
                                    </AgyleButton>
                                </div>
                            }
                        </div>

                        <div className={classes.divRow}>
                            <FormLabel style={{minWidth: 95}}>
                                {t('Home Leave')}
                            </FormLabel>

                            <AgyleBlockSwitch
                                value={form.homeLeave}
                                onChange={(val) => {
                                    if (val) handleChange(false, 'offWard');
                                    handleChange(val, 'homeLeave');
                                }}
                            />
                        </div>

                        <AgyleTextField
                            label={t('Information')}
                            rows={2}
                            multiline
                            value={form.information}
                            onChange={e => {
                                setModified(true);
                                setForm(prevState => ({
                                    ...prevState,
                                    information: e.target.value,
                                }));
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />

                        <AgyleDatePicker
                            label={t('Start Date')}
                            value={form.startDate}
                            onChange={(val) => handleChange(val, 'startDate')}
                            type="dateTime"
                            minDate={moment(calculateArrivalDate(patient))}
                            onError={(e) => setErrorDate(prevState => ({
                                ...prevState,
                                start: e,
                            }))}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />

                        <AgyleDatePicker
                            label={t('End Date')}
                            value={form.endDate}
                            onChange={(val) => handleChange(val, 'endDate')}
                            type="dateTime"
                            disableFuture={false}
                            onError={(e) => setErrorDate(prevState => ({
                                ...prevState,
                                end: e,
                            }))}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />

                        <div className={classes.divRow} style={{flexWrap: 'wrap', justifyContent: 'space-between'}}>
                            {renderButtons()}
                        </div>
                    </div>
                </>
            </MiniFormDialog>

            <MessageBox
                open={isConfirmOpen}
                title={t('Patient back on ward')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handlePatientBack}
                onClose={closeConfirm}
                message={t('Are you sure the patient has already returned to the ward?')}
            />

            <MessageBox
                open={isClearOpen}
                title={t('Clear data')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleClearData}
                onClose={closeClear}
                message={t('Are you sure you want to clear all the data from the format?')}
            />

            <MessageBox
                open={isWarningOpen}
                title={t('Warning')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={() => onSave(true)}
                onClose={closeWarning}
                message={t('You are trying to save the form when the patient has already returned to the ward. Do you want to save it again and mark the patient is not return to the ward?')}
            />
        </>
    );
};

export default OffWardForm;